import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "components/ThemeProvider";
import { Toaster } from "./components/ui/sonner";
import { useEffect, useState } from "react";
// Pages
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import AITutor from "./pages/AITutor";
import SkillPaths from "./pages/SkillPaths";
import SkillPathDetail from "./pages/SkillPathDetail";
import CoursePlayer from "./pages/CoursePlayer";
import Progress from "./pages/Progress";
import Community from "./pages/Community";
import Settings from "./pages/Settings";
import AdminPanel from "./pages/AdminPanel";
import SuperAdminPanel from "./pages/SuperAdminPanel";
import SuperAdminOrganizations from "./pages/SuperAdminOrganizations";
import SuperAdminModuleUploader from "./pages/SuperAdminModuleUploader";
import MyModules from "./pages/MyModules";
import ModuleViewer from "./pages/ModuleViewer";
import ModuleManagementPanel from './pages/ModuleManagementPanel'; 
import SuperAdminModules from './pages/SuperAdminModules';
import SubModuleViewer from "./pages/SubModuleViewer";
import LoginByCode from "./pages/LoginByCode";
import OrgAdminPanel from "./pages/OrgAdminPanel";
import ModulesCatalog from "./pages/ModulesCatalog";
// Layout
import MainLayout from "./components/layout/MainLayout";
import ModuleTestsManager from './pages/ModuleTestsManager';
import "./App.css";
import ScormPlayer from 'pages/ScormPlayer';
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse">
          <div className="w-16 h-16 rounded-full bg-primary/20" />
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div>Loading...</div>;
  if (user?.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return children;
};

const OrgAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div>Loading...</div>;
  if (user?.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return children;
};

const SuperAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div>Loading...</div>;
  if (user?.role !== 'superadmin') return <Navigate to="/dashboard" replace />;
  return children;
};

const FeatureRoute = ({ feature, children, redirectTo = "/dashboard" }) => {
  const { user, loading } = useAuth();
  if (loading) return <div>Loading...</div>;
  if (!user?.features?.[feature]) return <Navigate to={redirectTo} replace />;
  return children;
};

const RoleBasedRedirect = () => {
  const { user } = useAuth();
  if (user?.role === 'superadmin') return <Navigate to="/superadmin" replace />;
  if (user?.role === 'admin') return <Navigate to="/admin" replace />;
  return <Navigate to="/dashboard" replace />;
};

const AppRoutes = () => {
  const { isAuthenticated, loading } = useAuth();
  const [prefilledCode, setPrefilledCode] = useState('');

  // ← AÑADIR: detectar pending_code al montar
  const [hasPendingOtp, setHasPendingOtp] = useState(
    !!sessionStorage.getItem('pending_code')  // true si hay código pendiente
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const logout = params.get('logout');

    const pendingCode = sessionStorage.getItem('pending_code');
    const pendingHint = sessionStorage.getItem('pending_hint');

    if (logout || code) {
      localStorage.removeItem('token');
      sessionStorage.clear();
    }

    if (logout && pendingCode) {
      sessionStorage.setItem('pending_code', pendingCode);
      sessionStorage.setItem('pending_hint', pendingHint || '');
      setHasPendingOtp(true);
    }

    if (code) {
      setPrefilledCode(code.toUpperCase());
      window.history.replaceState({}, '', '/');
    }
  }, []);
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse">
          <div className="w-16 h-16 rounded-full bg-primary/20" />
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={
        isAuthenticated && !prefilledCode && !hasPendingOtp
          ? <RoleBasedRedirect />
          : <Landing prefilledCode={prefilledCode} onOtpHandled={() => setHasPendingOtp(false)} />
      } />

      <Route path="/dashboard" element={
        <ProtectedRoute><MainLayout><Dashboard /></MainLayout></ProtectedRoute>
      } />

      <Route path="/ai-tutor" element={
  <ProtectedRoute>
    <FeatureRoute feature="ai_tutor">
      <MainLayout><AITutor /></MainLayout>
    </FeatureRoute>
  </ProtectedRoute>
} />

      <Route path="/admin" element={
        <ProtectedRoute>
          <OrgAdminRoute>
            <MainLayout>
              <OrgAdminPanel />
            </MainLayout>
          </OrgAdminRoute>
        </ProtectedRoute>
      } />

      <Route path="/superadmin" element={
        <ProtectedRoute><SuperAdminRoute><MainLayout><SuperAdminPanel /></MainLayout></SuperAdminRoute></ProtectedRoute>
      } />

      <Route path="/superadmin/organizations" element={
        <ProtectedRoute><SuperAdminRoute><MainLayout><SuperAdminOrganizations /></MainLayout></SuperAdminRoute></ProtectedRoute>
      } />

      <Route path="/superadmin/upload-module" element={
        <ProtectedRoute><SuperAdminRoute><MainLayout><SuperAdminModuleUploader /></MainLayout></SuperAdminRoute></ProtectedRoute>
      } />

      <Route path="/superadmin/modules" element={
        <ProtectedRoute><SuperAdminRoute><MainLayout><SuperAdminModules /></MainLayout></SuperAdminRoute></ProtectedRoute>
      } />

      <Route path="/superadmin/settings" element={
        <ProtectedRoute><SuperAdminRoute><MainLayout><Settings /></MainLayout></SuperAdminRoute></ProtectedRoute>
      } />

      <Route path="/admin/modules/:moduleId/tests" element={
        <ProtectedRoute><MainLayout><ModuleTestsManager /></MainLayout></ProtectedRoute>
      } />

      <Route path="/admin/:orgId/modules" element={
        <ProtectedRoute><SuperAdminRoute><MainLayout><ModuleManagementPanel /></MainLayout></SuperAdminRoute></ProtectedRoute>
      } />

      <Route path="/catalog" element={
        <ProtectedRoute><MainLayout><ModulesCatalog /></MainLayout></ProtectedRoute>
      } />

      <Route path="/my-modules" element={
        <ProtectedRoute><MainLayout><MyModules /></MainLayout></ProtectedRoute>
      } />

      <Route path="/module/:id" element={
        <ProtectedRoute><MainLayout><ModuleViewer /></MainLayout></ProtectedRoute>
      } />

      <Route path="/modules/:moduleId" element={
        <ProtectedRoute><MainLayout><ModuleViewer /></MainLayout></ProtectedRoute>
      } />

      <Route path="/module/:moduleId/unit/:subModuleId" element={
        <ProtectedRoute><MainLayout><SubModuleViewer /></MainLayout></ProtectedRoute>
      } />

      <Route path="/modules/:moduleId/submodules/:subModuleId" element={
        <ProtectedRoute><MainLayout><SubModuleViewer /></MainLayout></ProtectedRoute>
      } />

      <Route path="/skill-paths" element={
        <ProtectedRoute><MainLayout><SkillPaths /></MainLayout></ProtectedRoute>
      } />

      <Route path="/skill-paths/:id" element={
        <ProtectedRoute><MainLayout><SkillPathDetail /></MainLayout></ProtectedRoute>
      } />

      <Route path="/course/:id" element={
        <ProtectedRoute><MainLayout hideSidebar><CoursePlayer /></MainLayout></ProtectedRoute>
      } />
      <Route path="/scorm-player/:moduleId" element={
        <ProtectedRoute><ScormPlayer /></ProtectedRoute>
      } />

      <Route path="/progress" element={
        <ProtectedRoute><MainLayout><Progress /></MainLayout></ProtectedRoute>
      } />

      <Route path="/community" element={
        <ProtectedRoute><MainLayout><Community /></MainLayout></ProtectedRoute>
      } />

      <Route path="/settings" element={
        <ProtectedRoute><MainLayout><Settings /></MainLayout></ProtectedRoute>
      } />

      <Route path="/login" element={
        isAuthenticated && !prefilledCode && !hasPendingOtp
          ? <RoleBasedRedirect />
          : <Landing prefilledCode={prefilledCode} onOtpHandled={() => setHasPendingOtp(false)} />
      } />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
  <ThemeProvider>
    <AppRoutes />
    <Toaster position="top-right" richColors />
  </ThemeProvider>
</AuthProvider>
    </BrowserRouter>
  );
}

export default App;
